import angular from 'angular';
import _ from 'lodash';
import Logger from '../../../shared/logger';
import {PRIORITY_VALUES} from '../../candidate/profile/section/matched-skills/item';

export const CandidateService = (Restangular, WorkflowService) => {
    const getCandidate = function (id) {
        return Restangular
            .one('candidate', id)
            .get('')
            .catch(error => Logger.error(error));
    };

    const searchCandidates = data => Restangular
        .all('search/candidates')
        .post(data)
        .catch(error => Logger.error(error));

    const getExtrapolatedSearchData = data => Restangular
        .all('talentScore/getExtrapolatedSearchData')
        .post(data)
        .catch(error => Logger.error(error));

    const postCandidate = function (data) {
        return Restangular
            .all('candidate')
            .post(data)
            .catch(error => Logger.error(error));
    };

    const postCandidateNote = function (candidateId, data) {
        return Restangular
            .one('candidate', candidateId)
            .all('note')
            .post(data)
            .catch(error => Logger.error(error));
    };

    const postCandidateComment = function (candidateId, data) {
        return Restangular
            .one('candidate', candidateId)
            .all('comment')
            .post(data)
            .catch(error => Logger.error(error));
    };

    const postCandidateInterview = function (candidateId, data) {
        return Restangular
            .one('candidate', candidateId)
            .all('interview')
            .post(data)
            .catch(error => Logger.error(error));
    };

    const postCandidateInterviewsSearch = function (candidateId, data) {
        return Restangular
            .one('candidate', candidateId)
            .all('interview')
            .all('search')
            .post(data)
            .catch(error => Logger.error(error));
    };

    const updateCandidateInterview = function (candidateId, interviewId, data) {
        return Restangular
            .one('candidate', candidateId)
            .one('interview', interviewId)
            .customPUT(data)
            .catch(error => Logger.error(error));
    };

    const getCandidateNotes = function (candidateId) {
        return Restangular
            .one('candidate', candidateId)
            .all('notes')
            .get('')
            .catch(error => Logger.error(error));
    };

    const putCandidateNotes = function (candidateId, noteId, data) {
        return Restangular
            .one('candidate', candidateId)
            .one('note', noteId)
            .customPUT(data)
            .catch(error => Logger.error(error));
    };

    const getCandidateComments = function (candidateId) {
        return Restangular
            .one('candidate', candidateId)
            .all('commentsWithAuthors')
            .get('')
            .catch(error => Logger.error(error));
    };

    const putCandidateComments = function (candidateId, commentId, data) {
        return Restangular
            .one('candidate', candidateId)
            .one('comment', commentId)
            .customPUT(data)
            .catch(error => Logger.error(error));
    };

    const postCandidateSearch = function (data) {
        return Restangular
            .all('candidate')
            .all('search')
            .post(data)
            .catch(error => Logger.error(error));
    };

    const deleteCandidateNote = function (candidateId, noteId) {
        return Restangular
            .one('candidate', candidateId)
            .one('note', noteId)
            .customDELETE()
            .catch(error => Logger.error(error));
    };

    const deleteCandidateComment = function (candidateId, commentId) {
        return Restangular
            .one('candidate', candidateId)
            .one('comment', commentId)
            .customDELETE()
            .catch(error => Logger.error(error));
    };

    const getCandidates = function (data) {
        return Restangular
            .all('candidate')
            .all('myCandidates')
            .post(data)
            .catch(error => Logger.error(error));
    };

    const extendFeedbackData = (candidate, feedbackProviders) => {
        const feedback = _.get(candidate, 'workflow.feedback', null) || _.get(candidate, 'sample', null);
        const feedbackStatus = _.get(feedback, 'status', null);
        const isFeedbackProviderExist = _.has(feedback, 'employerRepId') || _.has(feedback, 'recruiterId');

        if (!isFeedbackProviderExist) {
            return candidate;
        }

        const isSampleFeedback = !_.isEmpty(candidate.sample);
        const isFeedbackFromEmployerExists = WorkflowService.isFeedbackFromEmployerExists(feedbackStatus, null);
        const feedbackProviderId = feedback.employerRepId || feedback.recruiterId;
        const feedbackProviderName = feedbackProviders[feedbackProviderId];

        if (isFeedbackFromEmployerExists) {
            _.set(candidate, 'workflow.feedback.feedbackProviderName', feedbackProviderName);
        }

        if (isSampleFeedback) {
            _.set(candidate, 'sample.feedbackProviderName', feedbackProviderName);
        }

        return candidate;
    };

    const getCurrentJob = (candidateJobTitles, explainJobTitles) => _.chain(candidateJobTitles)
        .filter(({ isCurrent }) => isCurrent)
        .map((currentJob) => {
            const updatedTiles = _
                .chain(currentJob.titles)
                .map((jobTitle) => {
                    const jobTitlePredecessors = _.get(jobTitle, 'predecessors', []) || [];
                    const explainedTitle = _.find(explainJobTitles, title => [jobTitle.titleId, ...jobTitlePredecessors].includes(title.id));

                    if (explainedTitle) {
                        const { priority, priorityValue } = explainedTitle;

                        if (!priorityValue && !priority) {
                            Logger.error(`[candidateService.getCurentJob] explainedTitle is missing required fields (priorityValue, priority). explainedTitle => ${explainedTitle}`);
                        }
                        const priorityValueCalculated = priorityValue || PRIORITY_VALUES[(priority || '').toUpperCase()];

                        return {
                            ...jobTitle,
                            priorityValue: priorityValueCalculated,
                        };
                    }

                    return jobTitle;
                })
                .sortBy(jobTitle => -jobTitle.priorityValue)
                .value();

            return {
                ...currentJob,
                titles: updatedTiles,
            };
        })
        .sortBy(currentJob => -_.get(currentJob, 'titles.0.priorityValue', 0))
        .head()
        .value();

    const addExplainsDataToCandidates = ({ candidates, talentExplains }) => (candidates || []).map((candidate) => {
        const explain = _.find(talentExplains, { id: (candidate.talentID || candidate.talentId) });

        if (explain) {
            candidate.matchScore = explain.score;
            candidate.explainScore = explain.scoring;
            candidate.talentInfo = explain.talentInfo;
            candidate.selfIndex = explain.selfIndex;
            candidate.cascadingSearchGroup = explain.cascadingSearchGroup;

            const { criteria: jobTitles } = _.find(explain.scoring, { filterName: 'jobTitles' }) || {};
            const employmentHistory = _.get(candidate, 'employmentHistory', _.get(candidate, 'candidateData.employmentHistory'));
            if (!_.isEmpty(employmentHistory)) {
                candidate.currentJob = getCurrentJob(employmentHistory, jobTitles);
                candidate.previousJob = _.find(employmentHistory, { isCurrent: false });
            }

            if (candidate.candidateData) {
                const { allSkills, matchedSkills } = extractSkillsFromCandidate(candidate);

                candidate.skills = allSkills;
                candidate.candidateData.skills = allSkills;
                candidate.matchedSkills = matchedSkills;
            }

            return candidate;
        }

        return candidate;
    });

    function extractSkillsFromCandidate(candidate) {
        const hitSkills = [];
        const filterSkills = [];
        const candidateSkills = candidate.candidateData.skills;
        const explainSkills = _.find(candidate.explainScore, { filterName: 'skills' });

        const fullFillSkillId = (skillExplain) => {
            if (!skillExplain.id) {
                skillExplain.id = (skillExplain.text || skillExplain.skillName || '').toLowerCase();
            }
        };

        if (explainSkills) {
            const explainSkillsCriteria = (explainSkills.criteria || []).map(skill => ({
                ...skill,
                priorityValue: skill.priorityValue || PRIORITY_VALUES[skill.priority.toUpperCase()],
            }));

            const skillExplainList = _.orderBy(explainSkillsCriteria, ['priorityValue'], ['desc']);

            for (let j = 0; j < skillExplainList.length; j++) {
                const skillExplain = skillExplainList[j];
                const isMatchedSkill = (skillExplain.priorityValue * skillExplain.score) > 0;
                if (isMatchedSkill) {
                    if (skillExplain.hasOwnProperty('group')) {
                        for (const matchedGroupSkill of skillExplain.group) {
                            fullFillSkillId(matchedGroupSkill);
                            const matchedSkill = _.remove(candidateSkills, { skillId: matchedGroupSkill.id });
                            if (matchedSkill && matchedSkill.length > 0) {
                                matchedSkill[0].priorityValue = skillExplain.priorityValue;
                                hitSkills.push(matchedSkill[0]);
                            }
                        }
                    } else {
                        fullFillSkillId(skillExplain);
                        const matchedSkill = _.remove(candidateSkills, { skillId: skillExplain.id });
                        if (matchedSkill && matchedSkill.length > 0) {
                            matchedSkill[0].priorityValue = skillExplain.priorityValue;
                            hitSkills.push(matchedSkill[0]);
                        }
                    }
                }

                const isFilterSkill = skillExplain.type && skillExplain.type == 'filter';
                if (isFilterSkill) {
                    filterSkills.push(skillExplain);
                }
            }
        }
        const matchedSkills = _.uniqWith(filterSkills.concat(hitSkills), (curr, other) => ((curr || {}).skillName || '').toLowerCase() === ((other || {}).skillName || '').toLowerCase());
        const allSkills = matchedSkills.concat(candidateSkills);
        return { allSkills, matchedSkills };
    }


    return {
        addExplainsDataToCandidates,
        getCandidate,
        postCandidate,
        postCandidateNote,
        postCandidateComment,
        postCandidateInterview,
        updateCandidateInterview,
        postCandidateInterviewsSearch,
        getCandidateNotes,
        putCandidateNotes,
        getCandidateComments,
        putCandidateComments,
        postCandidateSearch,
        deleteCandidateNote,
        deleteCandidateComment,
        getCandidates,
        extendFeedbackData,
        searchCandidates,
        getExtrapolatedSearchData,
        extractSkillsFromCandidate,
    };
};

angular
    .module('TALENYA-FE')
    .factory('CandidateService', CandidateService);
